import { useState } from "react";
import { canDo } from "@shared/roles";
import { useVehicles, useCreateVehicle, useUpdateVehicle, useDeleteVehicle } from "@/hooks/use-inventory";
import { useAuth } from "@/hooks/use-auth";
import { useAgences } from "@/hooks/use-agences";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Truck, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Vehicle } from "@shared/schema";

export default function VehicleList() {
  const { profile } = useAuth();
  const { data: vehicles, isLoading } = useVehicles();
  const { data: agences = [] } = useAgences();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const { toast } = useToast();
  const createMutation = useCreateVehicle();
  const updateMutation = useUpdateVehicle();

  const canEdit = canDo(profile?.accessRole) || profile?.role === 'planification';

  const [formData, setFormData] = useState({
    licensePlate: "",
    vehicleType: "",
    brandModel: "",
    status: "disponible" as const,
    nature: "parc" as "parc" | "transport",
    agenceId: null as string | null,
  });

  const [editData, setEditData] = useState({
    licensePlate: "",
    vehicleType: "",
    brandModel: "",
    status: "disponible" as string,
    nature: "parc" as "parc" | "transport",
    agenceId: null as string | null,
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const deleteMutation = useDeleteVehicle();

  const filteredVehicles = vehicles?.filter((v: Vehicle) =>
    v.licensePlate.toLowerCase().includes(search.toLowerCase()) ||
    v.brandModel.toLowerCase().includes(search.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync(formData as any);
      setCreateOpen(false);
      toast({ title: "Véhicule ajouté", description: "Le véhicule a été enregistré" });
       setFormData({ licensePlate: "", vehicleType: "", brandModel: "", status: "disponible", nature: "parc", agenceId: null });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de créer le véhicule" });
    }
  };

  const openEdit = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setEditData({
      licensePlate: vehicle.licensePlate,
      vehicleType: vehicle.vehicleType,
      brandModel: vehicle.brandModel,
      status: vehicle.status,
      nature: vehicle.nature,
      agenceId: (vehicle as any).agenceId ?? null,
    });
    setEditOpen(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVehicle) return;
    try {
      await updateMutation.mutateAsync({ id: selectedVehicle.id, ...editData } as any);
      setEditOpen(false);
      toast({ title: "Véhicule modifié", description: "Les informations ont été mises à jour" });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de modifier le véhicule" });
    }
  };

  const handleDelete = async () => {
    if (!vehicleToDelete) return;
    try {
      await deleteMutation.mutateAsync(vehicleToDelete.id);
      setDeleteConfirmOpen(false);
      setVehicleToDelete(null);
      toast({ title: "Véhicule supprimé", description: "Le véhicule a été retiré de la flotte" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Suppression impossible", description: err.message });
    }
  };

  const parkVehicles = vehicles?.filter((v: Vehicle) => v.nature === "parc") || [];
  const stats = {
    total: parkVehicles.length,
    disponible: parkVehicles.filter((v: Vehicle) => v.status === "disponible").length,
    en_mission: parkVehicles.filter((v: Vehicle) => v.status === "en_mission").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Véhicules</h1>
          <p className="text-muted-foreground mt-1">Gérer la flotte de véhicules</p>
        </div>
        {canEdit && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="shadow-lg shadow-primary/20" data-testid="button-add-vehicle">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Véhicule
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Nouveau Véhicule</DialogTitle>
                <DialogDescription>Enregistrer un nouveau véhicule dans la flotte.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Immatriculation</Label>
                    <Input data-testid="input-license-plate" required={formData.nature === "parc"} value={formData.licensePlate} onChange={e => setFormData({ ...formData, licensePlate: e.target.value })} placeholder={formData.nature === "parc" ? "AA-123-BB" : "Facultative"} />
                  </div>
                  <div className="space-y-2">
                    <Label>Type de véhicule</Label>
                    <Input data-testid="input-vehicle-type" required value={formData.vehicleType} onChange={e => setFormData({ ...formData, vehicleType: e.target.value })} placeholder="Utilitaire, Camionnette..." />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Nature</Label>
                  <Select value={formData.nature} onValueChange={(v: "parc" | "transport") => setFormData({ ...formData, nature: v, agenceId: v === "transport" ? null : formData.agenceId })}>
                    <SelectTrigger data-testid="select-vehicle-nature"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="parc">Ressource de parc</SelectItem><SelectItem value="transport">Mode de transport</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Marque / Modèle</Label>
                  <Input data-testid="input-brand-model" required value={formData.brandModel} onChange={e => setFormData({ ...formData, brandModel: e.target.value })} placeholder="Renault Kangoo, Peugeot Partner..." />
                </div>
                {formData.nature === "parc" && <div className="space-y-2">
                  <Label>Agence</Label>
                  <Select value={formData.agenceId ?? "none"} onValueChange={v => setFormData({ ...formData, agenceId: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="select-vehicle-agency"><SelectValue placeholder="Aucune agence" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Aucune agence</SelectItem>{agences.map(a => <SelectItem key={a.id} value={a.id}>{a.code} · {a.libelle}</SelectItem>)}</SelectContent>
                  </Select>
                </div>}
                <div className="pt-4 flex justify-end gap-2">
                  <Button variant="outline" type="button" onClick={() => setCreateOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={createMutation.isPending} data-testid="button-submit-vehicle">
                    {createMutation.isPending ? "Création..." : "Créer le véhicule"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card rounded-xl border p-4 shadow-sm">
          <p className="text-sm text-muted-foreground">Total</p>
          <p className="text-2xl font-bold" data-testid="text-stat-total">{stats.total}</p>
        </div>
        <div className="bg-card rounded-xl border p-4 shadow-sm">
          <p className="text-sm text-emerald-600">Disponibles</p>
          <p className="text-2xl font-bold text-emerald-600">{stats.disponible}</p>
        </div>
        <div className="bg-card rounded-xl border p-4 shadow-sm">
          <p className="text-sm text-amber-600">En mission</p>
          <p className="text-2xl font-bold text-amber-600">{stats.en_mission}</p>
        </div>
      </div>

      <div className="flex items-center gap-4 bg-card p-2 rounded-xl border shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="input-search-vehicles"
            placeholder="Rechercher par immatriculation ou modèle..."
            className="pl-9 border-none bg-transparent focus-visible:ring-0"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Véhicule</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Agence</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                  <TableCell colSpan={5} className="text-center h-32">Chargement...</TableCell>
              </TableRow>
            ) : filteredVehicles?.map((vehicle: Vehicle) => (
              <TableRow key={vehicle.id} className="group hover:bg-secondary/20 transition-colors" data-testid={`row-vehicle-${vehicle.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center border shadow-sm">
                      <Truck className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                       <div className="flex items-center gap-2"><span className="font-semibold text-foreground">{vehicle.brandModel}</span><Badge variant={vehicle.nature === "parc" ? "secondary" : "outline"}>{vehicle.nature === "parc" ? "Parc" : "Transport"}</Badge></div>
                       {vehicle.nature === "parc" && <div className="text-xs text-muted-foreground font-mono">{vehicle.licensePlate}</div>}
                    </div>
                  </div>
                </TableCell>
                 <TableCell className="text-muted-foreground">{vehicle.vehicleType}</TableCell>
                 <TableCell className="text-xs font-medium">{agences.find(a => a.id === (vehicle as any).agenceId)?.code || "—"}</TableCell>
                <TableCell>
                  {vehicle.nature === "parc" ? <StatusBadge status={vehicle.status} type="vehicle" /> : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  {canEdit && (
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(vehicle)}
                        data-testid={`button-edit-vehicle-${vehicle.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => { setVehicleToDelete(vehicle); setDeleteConfirmOpen(true); }}
                        data-testid={`button-delete-vehicle-${vehicle.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && (!filteredVehicles || filteredVehicles.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Aucun véhicule trouvé</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Supprimer le véhicule</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>{vehicleToDelete?.brandModel}</strong> ({vehicleToDelete?.licensePlate}) ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={handleDelete}
              data-testid="button-confirm-delete-vehicle"
            >
              {deleteMutation.isPending ? "Suppression..." : "Supprimer définitivement"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Modifier le véhicule</DialogTitle>
            <DialogDescription>Mettre à jour les informations du véhicule.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Immatriculation</Label>
                 <Input data-testid="input-edit-license" required={editData.nature === "parc"} value={editData.nature === "transport" && editData.licensePlate.startsWith("TRANSPORT-") ? "" : editData.licensePlate} onChange={e => setEditData({ ...editData, licensePlate: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Type de véhicule</Label>
                <Input data-testid="input-edit-type" required value={editData.vehicleType} onChange={e => setEditData({ ...editData, vehicleType: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nature</Label>
              <Select value={editData.nature} onValueChange={(v: "parc" | "transport") => setEditData({ ...editData, nature: v, agenceId: v === "transport" ? null : editData.agenceId })}>
                <SelectTrigger data-testid="select-edit-nature"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="parc">Ressource de parc</SelectItem><SelectItem value="transport">Mode de transport</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Marque / Modèle</Label>
              <Input data-testid="input-edit-brand" required value={editData.brandModel} onChange={e => setEditData({ ...editData, brandModel: e.target.value })} />
            </div>
            {editData.nature === "parc" && <div className="space-y-2">
              <Label>Statut</Label>
              <Select value={editData.status} onValueChange={v => setEditData({ ...editData, status: v })}>
                <SelectTrigger data-testid="select-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="disponible">Disponible</SelectItem>
                  <SelectItem value="en_mission">En mission</SelectItem>
                </SelectContent>
              </Select>
            </div>}
                {editData.nature === "parc" && <div className="space-y-2">
                  <Label>Agence</Label>
                  <Select value={editData.agenceId ?? "none"} onValueChange={v => setEditData({ ...editData, agenceId: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="select-edit-vehicle-agency"><SelectValue placeholder="Aucune agence" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Aucune agence</SelectItem>{agences.map(a => <SelectItem key={a.id} value={a.id}>{a.code} · {a.libelle}</SelectItem>)}</SelectContent>
                  </Select>
                </div>}
            <div className="pt-4 flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setEditOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={updateMutation.isPending} data-testid="button-submit-edit-vehicle">
                {updateMutation.isPending ? "Mise à jour..." : "Enregistrer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
